<template>
  <div class="seckill-index-container">
    <div class="main-content">
      <!-- 极速秒杀 Banner 区域 -->
      <div class="status-banner">
        <div class="banner-left">
          <div class="flash-icon"><i class="el-icon-lightning" /></div>
          <div>
            <h2 class="banner-title">{{ bannerTitle }}</h2>
            <p class="banner-sub">限时限量 爆款低至 1 折起</p>
          </div>
        </div>

        <div v-if="timeRemaining > 0" class="countdown-wrapper">
          <span class="countdown-label">距本场结束仅剩</span>
          <div class="time-boxes">
            <span class="time-block">{{ timeObj.h }}</span>
            <span class="time-colon">:</span>
            <span class="time-block">{{ timeObj.m }}</span>
            <span class="time-colon">:</span>
            <span class="time-block">{{ timeObj.s }}</span>
          </div>
        </div>
      </div>

      <!-- 秒杀商品网格区域 -->
      <el-skeleton :loading="loading" animated :count="8">
        <template #template>
          <el-row :gutter="20">
            <el-col v-for="i in 8" :key="i" :xs="24" :sm="12" :md="8" :lg="6">
              <el-card shadow="never" class="skeleton-card">
                <el-skeleton-item variant="image" style="width: 100%; height: 220px; border-radius: 8px;" />
                <div style="padding: 16px 0 0 0;">
                  <el-skeleton-item variant="h3" style="width: 85%;" />
                  <el-skeleton-item variant="rect" style="width: 100%; height: 12px; margin-top: 16px; border-radius: 6px;" />
                  <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 20px;">
                    <el-skeleton-item variant="text" style="width: 40%; height: 24px;" />
                    <el-skeleton-item variant="button" style="width: 90px; height: 32px;" />
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>
        </template>

        <template>
          <el-row v-if="seckillList.length > 0" :gutter="20">
            <el-col
              v-for="item in seckillList"
              :key="item.skuId"
              :xs="24"
              :sm="12"
              :md="8"
              :lg="6"
            >
              <!-- 秒杀商品卡片 -->
              <div class="goods-card-wrapper" @click="navToDetail(item.skuId)">
                <el-card class="goods-card" shadow="hover" :body-style="{ padding: '0px' }">
                  <!-- 商品图片及角标 -->
                  <div class="image-wrapper">
                    <img :src="item.picUrl" class="goods-img" alt="商品图片">
                    <div class="badge-tag">
                      <i class="el-icon-lightning" /> 限时秒杀
                    </div>
                    <div v-if="item.stock <= 0" class="sold-out-mask">
                      <span>已抢光</span>
                    </div>
                  </div>

                  <!-- 卡片内容体 -->
                  <div class="goods-info">
                    <div class="goods-title" :title="item.title">{{ item.title }}</div>

                    <!-- 抢购进度条 -->
                    <div class="progress-box">
                      <div class="progress-info">
                        <span class="progress-text">已抢 {{ calculatePercent(item) }}%</span>
                        <span class="stock-text">仅剩 {{ item.stock }} 件</span>
                      </div>
                      <el-progress
                        :percentage="calculatePercent(item)"
                        :show-text="false"
                        :color="item.stock > 0 ? '#ff4d4f' : '#909399'"
                        :stroke-width="8"
                      />
                    </div>

                    <!-- 价格与抢购按钮 -->
                    <div class="price-action">
                      <div class="price-box">
                        <div class="seckill-price">
                          <span class="currency">￥</span>
                          <span class="big-price">{{ formatPrice(item.seckillPrice)[0] }}</span>
                          <span class="small-price">.{{ formatPrice(item.seckillPrice)[1] }}</span>
                        </div>
                        <span class="original-price">￥{{ item.originalPrice }}</span>
                      </div>

                      <el-button
                        :type="item.stock > 0 ? 'danger' : 'info'"
                        size="small"
                        class="buy-btn"
                        :disabled="item.stock <= 0"
                      >
                        {{ item.stock > 0 ? '立即抢购' : '已售罄' }}
                      </el-button>
                    </div>
                  </div>
                </el-card>
              </div>
            </el-col>
          </el-row>

          <!-- 空状态展示 -->
          <div v-else class="empty-wrapper">
            <el-empty description="暂无正在进行中的秒杀活动" image-size="160" />
          </div>
        </template>
      </el-skeleton>
    </div>
  </div>
</template>

<script>
import { getSeckillProducts } from '@/api/mall'

export default {
  name: 'SeckillIndex',
  data() {
    return {
      loading: false,
      seckillList: [],
      bannerTitle: '本场秒杀 正在抢购中',
      timeRemaining: 0,
      timer: null
    }
  },
  computed: {
    timeObj() {
      const h = Math.floor(this.timeRemaining / 3600).toString().padStart(2, '0')
      const m = Math.floor((this.timeRemaining % 3600) / 60).toString().padStart(2, '0')
      const s = (this.timeRemaining % 60).toString().padStart(2, '0')
      return { h, m, s }
    }
  },
  created() {
    this.fetchSeckillGoods()
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    async fetchSeckillGoods() {
      this.loading = true
      try {
        const resp = await getSeckillProducts()
        if (resp.code === 0) {
          this.seckillList = resp.data.seckillProductList || []
          if (resp.data.endTime) {
            const end = new Date(resp.data.endTime).getTime()
            const now = new Date().getTime()
            this.timeRemaining = Math.max(0, Math.floor((end - now) / 1000))
            this.startTimer()
          }
        } else {
          this.$message.error(resp.msg || '获取秒杀列表失败')
        }
      } catch (err) {
        this.$message.error('网络开小差了，请刷新重试')
      } finally {
        this.loading = false
      }
    },

    startTimer() {
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        if (this.timeRemaining > 0) {
          this.timeRemaining--
        } else {
          clearInterval(this.timer)
          this.bannerTitle = '本场秒杀已结束'
        }
      }, 1000)
    },

    calculatePercent(item) {
      if (!item.initialStock || item.initialStock === 0) return 100
      const sold = item.initialStock - item.stock
      const percent = Math.floor((sold / item.initialStock) * 100)
      return Math.min(100, Math.max(0, percent))
    },

    formatPrice(price) {
      const p = parseFloat(price || 0).toFixed(2).split('.')
      return [p[0], p[1]]
    },

    navToDetail(skuId) {
      this.$router.push({
        path: `/mall/item?skuId=${skuId}`
      })
    }
  }
}
</script>

<style scoped>
.seckill-index-container {
  min-height: calc(100vh - 64px);
  background-color: #f6f8fa;
  padding-bottom: 40px;
}

.main-content {
  max-width: 1240px;
  margin: 24px auto;
  padding: 0 20px;
}

/* 炫彩秒杀 Banner */
.status-banner {
  background: linear-gradient(135deg, #2b1055 0%, #7597de 100%), linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
  background-blend-mode: overlay;
  background-size: cover;
  color: #ffffff;
  padding: 24px 32px;
  border-radius: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  box-shadow: 0 10px 25px rgba(255, 65, 108, 0.25);
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.flash-icon {
  width: 52px;
  height: 52px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  backdrop-filter: blur(8px);
}

.banner-title {
  font-size: 24px;
  font-weight: 800;
  margin: 0 0 4px 0;
  letter-spacing: 0.5px;
}

.banner-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
}

.countdown-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.countdown-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.time-boxes {
  display: flex;
  align-items: center;
  gap: 6px;
}

.time-block {
  background: #1f2429;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 6px;
  min-width: 36px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.time-colon {
  font-weight: bold;
  font-size: 16px;
  color: #ffffff;
}

/* 商品卡片 Grid 优化 */
.goods-card-wrapper {
  margin-bottom: 24px;
}

.goods-card {
  border-radius: 12px !important;
  border: 1px solid #edf2f7 !important;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
}

.goods-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08) !important;
  border-color: transparent !important;
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 220px;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.goods-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.4s ease;
}

.goods-card:hover .goods-img {
  transform: scale(1.05);
}

.badge-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(90deg, #ff416c, #ff4b2b);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(255, 65, 108, 0.4);
}

.sold-out-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sold-out-mask span {
  background: #303133;
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  padding: 6px 16px;
  border-radius: 20px;
}

.goods-info {
  padding: 16px;
  background-color: #ffffff;
}

.goods-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
  height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.progress-box {
  margin: 14px 0;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 6px;
}

.progress-text {
  color: #ff4d4f;
  font-weight: 600;
}

.stock-text {
  color: #909399;
}

.price-action {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 12px;
}

.seckill-price {
  color: #ff4d4f;
  font-weight: bold;
  line-height: 1;
}

.seckill-price .currency {
  font-size: 13px;
}

.seckill-price .big-price {
  font-size: 22px;
}

.seckill-price .small-price {
  font-size: 13px;
}

.original-price {
  font-size: 12px;
  color: #a0aec0;
  text-decoration: line-through;
  margin-left: 4px;
}

.buy-btn {
  border-radius: 8px !important;
  font-weight: bold !important;
  padding: 8px 14px !important;
  background: linear-gradient(90deg, #ff416c, #ff4b2b) !important;
  border: none !important;
  box-shadow: 0 4px 10px rgba(255, 65, 108, 0.3);
  transition: all 0.2s ease !important;
}

.buy-btn:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.buy-btn.is-disabled {
  background: #e2e8f0 !important;
  color: #a0aec0 !important;
  box-shadow: none !important;
}

.skeleton-card {
  border-radius: 12px !important;
  margin-bottom: 24px;
}

.empty-wrapper {
  padding: 60px 0;
}
</style>
